import {commonSort, formatter} from 'helpers';
import {Paging, Sorting} from 'helpers/constants';
import {inventoryItemActionTypes} from 'actions/actionTypes';

const initialState = {
  items: [
    {
      itemLocations: [],
      itemLocationsTableHidden: true,
      itemLocationsTableLoading: true,
      itemLocationsTableSortInfo: {
        sortColumn: 'reOrderPoint',
        sortOrder: Sorting.desc,
      },
    },
  ],
  totalRows: 0,
  isLoading: true,
  selectedCount: 0,
  allChecked: false,
  searchCriteria: '',
  previewItems: [],
  selectedFilter: {
    value: 'ShowAll',
    label: 'Show All',
  },
  pageInfo: {
    pageNumber: 1,
    pageSize: Paging.initialPageSize,
    pageSizes: Paging.pageSizes,
    totalPages: 1,
  },
  sortInfo: {
    sortColumn: 'qualifiedName',
    sortOrder: Sorting.asc,
  },
};

const inventoryStatus = item => {
  if (item.itemLocations.find(loc => loc.quantityOnHand < 0)) {
    return 'Negative Inventory';
  }

  if (
    item.itemLocations.find(
      loc =>
        loc.reOrderPoint !== null && loc.quantityOnHand <= loc.reOrderPoint,
    )
  ) {
    return 'Low Inventory';
  }

  if (
    item.itemLocations.find(
      loc => loc.reOrderPoint !== null || loc.quantityOnHand > 0,
    )
  ) {
    return 'Stocked';
  }

  return 'Not Stocked';
};

export default function inventoryItems(state = initialState, action) {
  switch (action.type) {
    case inventoryItemActionTypes.GET_INVENTORY_ITEM_SUCCESS: {
      const items = action.items.map(i => {
        const item = i;

        item.itemLocations = [];
        item.itemLocationsTableHidden = true;
        item.itemLocationsTableLoading = false;
        item.itemLocationsTableSortInfo = {
          sortColumn: 'reOrderPoint',
          sortOrder: Sorting.desc,
        };

        return item;
      });

      return {
        ...state,
        items,
        allChecked: false,
        selectedCount: 0,
        isLoading: false,
        totalRows: action.totalRows,
        pageInfo: {
          ...state.pageInfo,
          totalPages: Math.ceil(action.totalRows / state.pageInfo.pageSize),
        },
      };
    }

    case inventoryItemActionTypes.GET_PREVIEW_LIST_SUCCESS:
      return {...state, previewItems: action.items, isLoading: false};

    case inventoryItemActionTypes.INVENTORY_ITEM_PAGE_CHANGED:
      return {
        ...state,
        pageInfo: {
          ...state.pageInfo,
          pageSize: action.pageSize,
          pageNumber: action.pageNumber,
        },
      };

    case inventoryItemActionTypes.INVENTORY_ITEM_SORT_CHANGED:
      return {
        ...state,
        sortInfo: {
          sortOrder: action.sortOrder,
          sortColumn: action.sortColumn,
        },
      };

    case inventoryItemActionTypes.INVENTORY_ITEM_SEARCH:
      return {
        ...state,
        pageInfo: {
          ...state.pageInfo,
          pageNumber: 1,
        },
        searchCriteria: action.searchCriteria,
      };

    case inventoryItemActionTypes.INVENTORY_ITEM_FILTER_CHANGED:
      return {
        ...state,
        pageInfo: {
          ...state.pageInfo,
          pageNumber: 1,
        },
        selectedFilter: action.selectedFilter,
      };

    case inventoryItemActionTypes.INVENTORY_ITEM_CHECK_CHANGED: {
      const {items} = state;
      const allChecked = false;
      const updatedItems = items.map(i => {
        const item = i;

        if (item.partId !== action.itemId) return item;

        item.isChecked = !item.isChecked;

        return item;
      });
      const selectedCount = items.filter(i => !!i.isChecked).length;

      return {
        ...state,
        allChecked,
        selectedCount,
        items: updatedItems,
      };
    }

    case inventoryItemActionTypes.INVENTORY_ITEM_ALL_CHECK_CHANGED: {
      const updatedItems = state.items.map(i => ({
        ...i,
        isChecked: action.isChecked,
      }));
      return {
        ...state,
        allChecked: action.isChecked,
        selectedCount: action.isChecked ? updatedItems.length : 0,
        items: updatedItems,
      };
    }

    case inventoryItemActionTypes.INVENTORY_ITEM_OPEN_LOCATIONS_TABLE: {
      const {items} = state;
      const updatedItems = items.map(i => {
        const item = i;

        if (item.partId !== action.itemId) return item;

        item.itemLocationsTableHidden = false;
        item.itemLocationsTableLoading = true;

        return item;
      });

      return {...state, items: updatedItems};
    }

    case inventoryItemActionTypes.GET_INVENTORY_ITEM_LOCATIONS_TABLE_SUCCESS: {
      const {items} = state;
      const updatedItems = items.map(i => {
        const item = i;

        if (item.partId !== action.itemId) return item;

        const sortInfo = {
          sortColumn: 'reOrderPoint',
          sortOrder: Sorting.desc,
        };

        item.itemLocationsTableLoading = false;
        item.itemLocationsTableSortInfo = sortInfo;
        item.itemLocations = action.itemLocations.sort(commonSort(sortInfo));

        return item;
      });

      return {...state, items: updatedItems};
    }

    case inventoryItemActionTypes.INVENTORY_ITEM_CLOSE_LOCATIONS_TABLE: {
      const {items} = state;
      const updatedItems = items.map(i => {
        const item = i;

        if (item.partId !== action.itemId) return item;

        item.itemLocations = [];
        item.itemLocationsTableHidden = true;
        item.itemLocationsTableLoading = true;

        return item;
      });

      return {...state, items: updatedItems};
    }

    case inventoryItemActionTypes.INVENTORY_ITEM_LOCATIONS_TABLE_SORT_CHANGED: {
      const {items} = state;
      const updatedItems = items.map(i => {
        const item = i;

        if (item.partId !== action.itemId || item.itemLocations.length === 0)
          return item;

        const updatedSortInfo = {
          sortOrder: action.sortOrder,
          sortColumn: action.sortColumn,
        };
        const updatedLocations = item.itemLocations.sort(
          commonSort(updatedSortInfo),
        );

        item.itemLocations = updatedLocations;
        item.itemLocationsTableSortInfo = updatedSortInfo;

        return item;
      });

      return {...state, items: updatedItems};
    }

    case inventoryItemActionTypes.INVENTORY_ITEM_LOCATIONS_TABLE_ITEM_SUBMIT: {
      const updatedItems = state.items.map(item => {
        if (item.partId !== action.itemLocation.partId) return item;
        const updatedItem = item;
        const locations = updatedItem.itemLocations.map(
          x =>
            x.inventoryLocationId === action.itemLocation.inventoryLocationId
              ? action.itemLocation
              : x,
        );
        const location = updatedItem.itemLocations.find(
          x =>
            x.inventoryLocationId === action.itemLocation.inventoryLocationId,
        );
        const filteredLocations = locations.filter(x => x.id !== 0);

        if (action.property === 'quantityOnHand') {
          let sum = 0;

          for (const loc in filteredLocations) {
            sum += filteredLocations[loc][action.property];
          }

          updatedItem[action.property] = sum;
          updatedItem.formattedOnHand = formatter.formatNumber(
            Math.round(sum * 100) / 100,
          );
          updatedItem.totalValue = sum * updatedItem.defaultCost;
        }

        updatedItem.lowInventory =
          (action.property === 'quantityOnHand' &&
            action.value <= ((location && location.reOrderPoint) || 0)) ||
          (action.property === 'reOrderPoint' &&
            ((location && location.quantityOnHand) || 0) <= action.value);

        for (const loc in filteredLocations) {
          const {quantityOnHand, reOrderPoint} = filteredLocations[loc];

          if ((quantityOnHand || 0) < (reOrderPoint || 0)) {
            updatedItem.lowInventory = true;
            break;
          }
        }

        updatedItem.locations = filteredLocations.filter(
          x => x.quantityOnHand !== null || x.reOrderPoint !== null,
        ).length;
        updatedItem.itemLocations = locations;

        updatedItem.status = inventoryStatus(updatedItem);
        return updatedItem;
      });

      return {...state, items: updatedItems};
    }

    case inventoryItemActionTypes.INVENTORY_ITEM_LOADING:
      return {...state, isLoading: true};

    default:
      return state;
  }
}



// WEBPACK FOOTER //
// ./src/reducers/inventoryItemsReducer.js